import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  Fade,
  InputAdornment,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { MyButton } from '../index';
import { hasuraClient } from '../../utils/graphqlClient';
import { FORGOT_PASSWORD_MUTATION } from '../../graphql/mutations/auth';
import { useFormValidation, validationSchemas } from '../../utils/validation';
import { forgotPasswordModalStyles } from './ForgotPasswordModal.styles';

const ForgotPasswordModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const {
    data: formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    reset,
  } = useFormValidation({ email: '' }, {
    email: validationSchemas.login.email,
  });

  const handleInputChange = useCallback(
    field => event => {
      const value = event.target.value;
      handleChange(field, value);
      if (submitError) {
        setSubmitError(null);
      }
    },
    [handleChange, submitError]
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();

      const { isValid: formIsValid } = validateAll();
      if (!formIsValid) {
        return;
      }

      try {
        setIsSubmitting(true);
        setSubmitError(null);

        const response = await hasuraClient.mutate(
          FORGOT_PASSWORD_MUTATION,
          {
            input: {
              emailAddress: formData.email,
            },
          },
          {
            skipAuth: true, // Forgot password doesn't require authentication
          }
        );

        if (response?.forgotPassword?.success) {
          setSubmitSuccess(true);
          reset();
        } else {
          setSubmitError(
            response?.forgotPassword?.message ||
              t('auth.forgotPassword.errors.submitFailed', 'Şifre sıfırlama isteği gönderilemedi.')
          );
        }
      } catch (error) {
        setSubmitError(
          error.message ||
            t('auth.forgotPassword.errors.submitError', 'Bir hata oluştu. Lütfen tekrar deneyin.')
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData.email, validateAll, reset, t]
  );

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      reset();
      setSubmitSuccess(false);
      setSubmitError(null);
      onClose();
    }
  }, [isSubmitting, reset, onClose]);

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose(e, reason);
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      sx={forgotPasswordModalStyles.dialog(theme)}
      PaperProps={{
        sx: forgotPasswordModalStyles.paper(theme),
      }}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 300 }}
    >
      <DialogTitle sx={forgotPasswordModalStyles.header(theme)}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Box sx={forgotPasswordModalStyles.iconContainer(theme)}>
                {submitSuccess ? (
                  <CheckCircleIcon sx={{ fontSize: 32, color: 'white' }} />
                ) : (
                  <LockResetIcon sx={{ fontSize: 32, color: 'white' }} />
                )}
              </Box>
              <Typography variant="h5" sx={forgotPasswordModalStyles.title(theme)}>
                {submitSuccess 
                  ? t('auth.forgotPassword.success.title', 'E-posta Gönderildi!')
                  : t('auth.forgotPassword.title', 'Şifremi Unuttum')
                }
              </Typography>
              {!submitSuccess && (
                <Typography variant="body2" sx={forgotPasswordModalStyles.description}>
                  {t(
                    'auth.forgotPassword.description',
                    'E-posta adresinizi girin, size şifre sıfırlama bağlantısı gönderelim.'
                  )}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={isSubmitting}
            sx={{
              color: 'text.secondary',
              position: 'absolute',
              top: theme.spacing(2),
              right: theme.spacing(2),
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={forgotPasswordModalStyles.content(theme)}>
        {submitSuccess ? (
          <Fade in timeout={400}>
            <Box sx={forgotPasswordModalStyles.successContainer(theme)}>
              <Box sx={forgotPasswordModalStyles.successIconContainer(theme)}>
                <CheckCircleIcon 
                  sx={{ 
                    fontSize: 48, 
                    color: 'success.main',
                  }} 
                />
              </Box>
              <Typography variant="h6" sx={forgotPasswordModalStyles.successTitle(theme)}>
                {t('auth.forgotPassword.success.title', 'E-posta Gönderildi!')}
              </Typography>
              <Typography variant="body1" sx={forgotPasswordModalStyles.successMessage(theme)}>
                {t(
                  'auth.forgotPassword.success.message',
                  'Şifre sıfırlama bağlantısı e-posta adresinize gönderildi. Lütfen e-postanızı kontrol edin.'
                )}
              </Typography>
            </Box>
          </Fade>
        ) : (
          <Fade in timeout={400}>
            <Box>
              {submitError && (
                <Alert 
                  severity="error" 
                  sx={{ 
                    mb: 3, 
                    borderRadius: 2,
                  }} 
                  onClose={() => setSubmitError(null)}
                >
                  {submitError}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} noValidate sx={forgotPasswordModalStyles.formContainer(theme)}>
                <TextField
                  fullWidth
                  label={t('auth.login.email', 'E-posta')}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  onBlur={() => handleBlur('email')}
                  error={touched.email && !!errors.email}
                  helperText={touched.email && errors.email}
                  disabled={isSubmitting}
                  autoComplete="email"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  sx={forgotPasswordModalStyles.textField(theme)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon
                          sx={{ 
                            color: touched.email && errors.email 
                              ? 'error.main' 
                              : 'action.disabled',
                            fontSize: 20,
                          }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </Box>
          </Fade>
        )}
      </DialogContent>

      <DialogActions sx={forgotPasswordModalStyles.actions(theme)}>
        {submitSuccess ? (
          <MyButton
            variant="contained"
            onClick={handleClose}
            fullWidth
            sx={forgotPasswordModalStyles.submitButton(theme)}
          >
            {t('common.close')}
          </MyButton>
        ) : (
          <>
            <MyButton
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={forgotPasswordModalStyles.button(theme)}
            >
              {t('common.cancel')}
            </MyButton>
            <MyButton
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || !formData.email || !!errors.email}
              sx={forgotPasswordModalStyles.submitButton(theme)}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} color="inherit" />
                  {t('auth.forgotPassword.sending', 'Gönderiliyor...')}
                </>
              ) : (
                t('auth.forgotPassword.send', 'Gönder')
              )}
            </MyButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ForgotPasswordModal;

